import { makeAutoObservable } from 'mobx';

import { EditorStore } from '../editor/EditorStore';
import { AnalyticsStore } from './AnalyticsStore';
import { BRollStore } from './BRollStore';
import { DashboardStore } from './DashboardStore';
import { DatoClientStore } from './DatoClientStore';
import { DatoPersonStore } from './DatoPersonStore';
import { KaraokeStore } from './KaraokeStore';
import { LayoutStore } from './LayoutStore';
import { TemplatedPromptsStore } from './TemplatedPromptsStore';
import { TranscriptionStore } from './TranscriptionStore';
import { UserIdentityStore } from './UserIdentityStore';
import { VideoSourceStore } from './VideoSourceStore';

import type { DatoContext } from './types';

interface InitializeParams {
  env: string;
  datoContext?: DatoContext;
}

export class RootStore {
  // Child Stores
  userIdentityStore: UserIdentityStore;
  analyticsStore: AnalyticsStore;
  templatedPromptsStore: TemplatedPromptsStore;
  datoClientStore: DatoClientStore;
  transcriptionStore: TranscriptionStore;
  dashboardStore: DashboardStore;
  editorStore: EditorStore;
  videoSourceStore: VideoSourceStore;
  bRollStore: BRollStore;
  datoPersonStore: DatoPersonStore;
  layoutStore: LayoutStore;
  karaokeStore: KaraokeStore;

  // Base State
  initialized: boolean = false;
  storyId: string | undefined | null = null;

  constructor() {
    // Initialize child stores without dependencies
    this.userIdentityStore = new UserIdentityStore(this);
    this.analyticsStore = new AnalyticsStore(this);
    this.templatedPromptsStore = new TemplatedPromptsStore(this);
    this.datoClientStore = new DatoClientStore(this);
    this.transcriptionStore = new TranscriptionStore(this);
    this.dashboardStore = new DashboardStore(this);
    this.editorStore = new EditorStore(this);
    this.videoSourceStore = new VideoSourceStore(this);
    this.bRollStore = new BRollStore(this);
    this.datoPersonStore = new DatoPersonStore(this);
    this.layoutStore = new LayoutStore(this);
    this.karaokeStore = new KaraokeStore();

    makeAutoObservable(this, {
      userIdentityStore: false,
      analyticsStore: false,
      templatedPromptsStore: false,
      datoClientStore: false,
      editorStore: false,
      videoSourceStore: false,
      bRollStore: false,
      datoPersonStore: false,
      layoutStore: false,
      karaokeStore: false,
    });
  }

  // Initialize the store with the storyId, environment, and Dato context
  // This is a proper initialization sequence that ensures dependencies are initialized
  // in the correct order
  initialize({ env, datoContext }: InitializeParams) {
    // 1. Set up environment variables and core client settings
    this.userIdentityStore.setEnvironment(env);
    if (!this.initialized) {
      this.analyticsStore.initSmartlook();
    }

    // 2. Set up DatoContext if provided
    if (datoContext) {
      this.userIdentityStore.setDatoContext(datoContext);
    }

    // 3. Initialize DatoClientStore and repositories first
    //    This ensures all repositories are available
    this.datoClientStore.initializeClients();

    // 4. Mark initialization as complete
    this.initialized = true;
  }

  // We're ready if we're initialized and have either:
  // 1. Dato context
  // 2. Valid environment for external users
  // 3. API clients are ready
  get isReady(): boolean {
    return (
      this.initialized &&
      (this.userIdentityStore.hasDatoContext ||
        !!this.userIdentityStore.environment) &&
      this.datoClientStore.isReady
    );
  }

  // Helper method to get current auth type
  get authType(): 'dato' | 'clerk' | null {
    if (this.userIdentityStore.hasDatoContext) return 'dato';
    if (this.userIdentityStore.currentIdentity?.source === 'clerk')
      return 'clerk';
    return null;
  }

  // Helper to check if we have valid authentication
  get hasValidAuth(): boolean {
    return this.userIdentityStore.currentIdentity !== null;
  }
}
